import type { ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export type CrudDialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<CrudDialogSize, string> = {
  sm: 'max-w-96',
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
};

export interface CrudDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: string;
  size?: CrudDialogSize;
  children: ReactNode;
  footer?: ReactNode;
}

export function CrudDialog({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  children,
  footer,
}: CrudDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('flex max-h-[min(90vh,48rem)] flex-col gap-0 rounded-xl p-0', SIZE_MAP[size])}
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display text-title-md text-accent-ink leading-tight">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-body-md leading-relaxed text-neutral-500">
              {description}
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-(--color-border) bg-neutral-50/40 px-6 py-4">
            {footer}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
